/**
 * Color utility functions
 */

/**
 * Convert RGB color string to hex
 */
export const rgbToHex = (rgb) => {
  const match = rgb.match(/^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/);
  if (!match) return null;

  const [, r, g, b] = match;
  return (
    "#" +
    [r, g, b]
      .map((x) => {
        const hex = parseInt(x).toString(16);
        return hex.length === 1 ? "0" + hex : hex;
      })
      .join("")
  );
};

/**
 * Convert hex color to RGB values
 */
export const hexToRgb = (hex) => {
  // Remove # if present
  hex = hex.replace("#", "");
  
  // Handle shorthand hex (e.g., #fff)
  if (hex.length === 3) {
    hex = hex.split("").map((char) => char + char).join("");
  }
  
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  
  return { r, g, b };
};

/**
 * Convert RGB string to RGB object
 */
export const parseRgb = (rgbString) => {
  const match = rgbString.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)/);
  if (!match) return null;
  
  return {
    r: parseInt(match[1]),
    g: parseInt(match[2]),
    b: parseInt(match[3]),
  };
};

/**
 * Calculate luminance of a color (0-1, where 1 is brightest)
 * Uses relative luminance formula from WCAG
 */
export const getLuminance = (color) => {
  let r, g, b;
  
  if (color.startsWith("#")) {
    const rgb = hexToRgb(color);
    r = rgb.r;
    g = rgb.g;
    b = rgb.b;
  } else if (color.startsWith("rgb")) {
    const rgb = parseRgb(color);
    if (!rgb) return 0.5; // Default to medium if parsing fails
    r = rgb.r;
    g = rgb.g;
    b = rgb.b;
  } else {
    // Unknown format, default to medium
    return 0.5;
  }
  
  // Normalize RGB values to 0-1
  const [rs, gs, bs] = [r, g, b].map((val) => {
    val = val / 255;
    return val <= 0.03928 ? val / 12.92 : Math.pow((val + 0.055) / 1.055, 2.4);
  });
  
  // Calculate relative luminance
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
};

/**
 * Check if a color is light (high luminance)
 * Returns true if the color is light enough to be invisible on white background
 */
export const isLightColor = (color) => {
  if (!color) return false;
  
  // Common light color names
  const lightColorNames = [
    "white",
    "yellow",
    "lightyellow",
    "lightyellow",
    "wheat",
    "lemonchiffon",
    "lightgoldenrodyellow",
    "palegoldenrod",
    "khaki",
    "gold",
    "cornsilk",
    "blanchedalmond",
    "bisque",
    "navajowhite",
    "peachpuff",
    "moccasin",
    "papayawhip",
    "lightcyan",
    "lightblue",
    "lightsteelblue",
    "lavender",
    "thistle",
    "plum",
    "mistyrose",
    "lavenderblush",
    "linen",
    "oldlace",
    "seashell",
    "ivory",
    "honeydew",
    "mintcream",
    "azure",
    "aliceblue",
    "ghostwhite",
    "snow",
    "floralwhite",
  ];
  
  const colorLower = color.toLowerCase().trim();
  
  // Check if it's a named light color
  if (lightColorNames.includes(colorLower)) {
    return true;
  }
  
  // Check luminance for hex/rgb colors
  const luminance = getLuminance(color);
  // Consider colors with luminance > 0.7 as light (will be hard to see on white)
  return luminance > 0.7;
};
